export type ServiceDirection = "purchase" | "sale";
type ServiceFacts = { direction: ServiceDirection; legalEntityId: string; counterpartyId: string;
  lines: Array<{ id: string; serviceId: string; quantity: number; unitPriceMinor: number; lineTotalMinor: number; productVersion: number }>;
  totalMinor: number; paidMinor: number; allocations: Array<{ id: string; sourceKind: string; sourceId: string; amountMinor: number }> };
export type ServicePendingCommand = {
  actorId: string; role: "admin"; key: string; path: string;
  payload?: Record<string, unknown>; version?: number; dispatched: boolean;
  expectedDirection?: ServiceDirection; expectedDocument?: ServiceFacts;
};
type StorageLike = Pick<Storage, "getItem" | "setItem" | "removeItem">;
const uuid = "[0-9a-f]{8}-(?:[0-9a-f]{4}-){3}[0-9a-f]{12}";
const validId = (value: unknown): value is string => typeof value === "string" && new RegExp(`^${uuid}$`).test(value);
const pathPattern = new RegExp(`^/api/v1/services/documents(?:/${uuid}/(?:post|reverse|allocations(?:/${uuid}/reverse)?))?$`);
export const serviceRecoveryPrefix = "pharmhub:service-command:";
const slot = (actorId: string) => `${serviceRecoveryPrefix}${encodeURIComponent(actorId)}:admin`;
const uncertain = "Результат предыдущей операции не подтверждён. Сначала проверьте его.";
const object = (value: unknown): value is Record<string, unknown> => value !== null && typeof value === "object" && !Array.isArray(value);
const direction = (value: unknown): value is ServiceDirection => value === "purchase" || value === "sale";
const money = (value: unknown): value is number => typeof value === "number" && Number.isSafeInteger(value) && value >= 0 && value <= 9_000_000_000_000;
const whole = (value: unknown): value is number => money(value) && value >= 1 && value <= 999_999;
const text = (value: unknown): value is string => typeof value === "string" && value.trim().length > 0;

function parse(raw: string, actorId?: string): ServicePendingCommand {
  const value: unknown = JSON.parse(raw);
  if (!object(value) || !text(value.actorId) || value.actorId.length > 160 || actorId !== undefined && value.actorId !== actorId
    || value.role !== "admin" || !validId(value.key) || typeof value.path !== "string" || !pathPattern.test(value.path)
    || value.payload !== undefined && !object(value.payload) || typeof value.dispatched !== "boolean"
    || value.version !== undefined && (!Number.isSafeInteger(value.version) || Number(value.version) < 1)
    || value.expectedDirection !== undefined && !direction(value.expectedDirection)
    || value.expectedDocument !== undefined && !object(value.expectedDocument)
    || Object.keys(value).some(key => !["actorId", "role", "key", "path", "payload", "version", "dispatched", "expectedDirection", "expectedDocument"].includes(key))) throw new Error(uncertain);
  const hasDocument = value.path !== "/api/v1/services/documents";
  if (hasDocument !== (value.version !== undefined)) throw new Error(uncertain);
  return value as ServicePendingCommand;
}

export function readServiceCommand(actorId: string, storage: StorageLike = sessionStorage): ServicePendingCommand | null {
  if (!actorId) throw new Error("Для операции нужна действующая учётная запись");
  const raw = storage.getItem(slot(actorId));
  return raw ? parse(raw, actorId) : null;
}

export function redactServiceCommand(raw: string): string {
  try {
    const value = parse(raw);
    const expectedDirection = value.expectedDirection ?? (direction(value.payload?.direction) ? value.payload.direction : undefined);
    return JSON.stringify({ actorId: value.actorId, role: value.role, key: value.key, path: value.path,
      version: value.version, dispatched: value.dispatched, expectedDirection });
  } catch { return '{"unresolved":true}'; }
}

function save(value: ServicePendingCommand, storage: StorageLike) {
  const raw = JSON.stringify(value); storage.setItem(slot(value.actorId), raw);
  if (storage.getItem(slot(value.actorId)) !== raw) throw new Error("Не удалось сохранить команду в браузере. Изменения не отправлены.");
}

function facts(value: Record<string, unknown>): ServiceFacts | null {
  if (!direction(value.direction) || !validId(value.legalEntityId) || !validId(value.counterpartyId)
    || !money(value.totalMinor) || !money(value.paidMinor) || !money(value.balanceMinor)
    || !Array.isArray(value.lines) || value.lines.length < 1 || value.lines.length > 200 || !Array.isArray(value.allocations)) return null;
  const ids = new Set<string>(), services = new Set<string>(); let total = 0;
  const lines: ServiceFacts["lines"] = [];
  for (const line of value.lines) {
    if (!object(line) || !validId(line.id) || !validId(line.serviceId) || !text(line.serviceName)
      || line.itemType !== "SERVICE" || !Number.isSafeInteger(line.productVersion) || Number(line.productVersion) < 1
      || !whole(line.quantity) || !money(line.unitPriceMinor) || line.unitPriceMinor > 999_999_999_999 || !money(line.lineTotalMinor)
      || line.lineTotalMinor !== line.quantity * line.unitPriceMinor || ids.has(line.id) || services.has(line.serviceId)) return null;
    ids.add(line.id); services.add(line.serviceId); total += line.lineTotalMinor;
    lines.push({ id: line.id, serviceId: line.serviceId, quantity: line.quantity, unitPriceMinor: line.unitPriceMinor, lineTotalMinor: line.lineTotalMinor, productVersion: Number(line.productVersion) });
  }
  if (!money(total) || total < 1 || total !== value.totalMinor || value.paidMinor > total) return null;
  const allocations: ServiceFacts["allocations"] = []; const allocationIds = new Set<string>(); let paid = 0;
  for (const allocation of value.allocations) {
    if (!object(allocation) || !validId(allocation.id) || !validId(allocation.sourceId)
      || !["payment", "company_cash"].includes(String(allocation.sourceKind)) || allocation.status !== "active"
      || !money(allocation.amountMinor) || allocation.amountMinor < 1 || allocationIds.has(allocation.id)) return null;
    allocationIds.add(allocation.id); paid += allocation.amountMinor;
    allocations.push({ id: allocation.id, sourceKind: String(allocation.sourceKind), sourceId: allocation.sourceId, amountMinor: allocation.amountMinor });
  }
  if (!money(paid) || paid !== value.paidMinor || value.balanceMinor !== (value.status === "posted" ? total - paid : 0)
    || value.status !== "posted" && (paid !== 0 || allocations.length !== 0)) return null;
  return { direction: value.direction, legalEntityId: value.legalEntityId, counterpartyId: value.counterpartyId, lines, totalMinor: total, paidMinor: paid, allocations };
}

export function serviceDocumentExpectation(value: unknown): ServiceFacts {
  const result = object(value) ? facts(value) : null;
  if (!result) throw new Error("Исходный акт не подтверждён. Откройте его заново.");
  return result;
}

export function prepareServiceCommand(actorId: string, input: Pick<ServicePendingCommand, "path" | "payload" | "version" | "expectedDirection" | "expectedDocument">, storage: StorageLike = sessionStorage): ServicePendingCommand {
  if (!pathPattern.test(input.path) || !object(input.payload) || !direction(input.expectedDirection)) throw new Error("Недопустимая операция услуги");
  const previous = readServiceCommand(actorId, storage);
  if (previous) {
    if (!previous.payload || previous.path !== input.path || previous.version !== input.version || JSON.stringify(previous.payload) !== JSON.stringify(input.payload)) throw new Error(uncertain);
    return previous;
  }
  const result: ServicePendingCommand = { actorId, role: "admin", key: crypto.randomUUID(),
    ...JSON.parse(JSON.stringify(input)) as typeof input, dispatched: false };
  parse(JSON.stringify(result), actorId); save(result, storage); return result;
}

export function dispatchServiceCommand(value: ServicePendingCommand, storage: StorageLike = sessionStorage) {
  const current = readServiceCommand(value.actorId, storage);
  if (!value.payload || !current?.payload || current.key !== value.key || current.path !== value.path || current.version !== value.version
      || JSON.stringify(current.payload) !== JSON.stringify(value.payload)) throw new Error(uncertain);
  save({ ...current, dispatched: true }, storage);
}

export function clearServiceCommand(actorId: string, key: string, storage: StorageLike = sessionStorage) {
  if (readServiceCommand(actorId, storage)?.key === key) storage.removeItem(slot(actorId));
}

const sorted = <T extends { id: string }>(rows: T[]) => [...rows].sort((a, b) => a.id.localeCompare(b.id));
export function validateServiceResult(value: unknown, command: ServicePendingCommand): value is { id: string; version: number; nonFiscal: true } {
  if (!object(value)) return false;
  const id = command.path.split("/")[5], event = command.path.split("/").slice(6).join("/");
  const expectedStatus = !id ? "draft" : event === "reverse" ? "reversed" : "posted";
  const expectedDirection = command.expectedDirection ?? (direction(command.payload?.direction) ? command.payload.direction : undefined);
  if (!validId(value.id) || id && id !== value.id || !Number.isSafeInteger(value.version)
    || value.version !== (command.version ?? 0) + 1 || value.nonFiscal !== true || !text(value.number)
    || value.status !== expectedStatus || !expectedDirection || value.direction !== expectedDirection) return false;
  const result = facts(value); if (!result) return false;
  if (!command.payload) return true; // Only authenticated, actor/key-scoped GET recovery uses a redacted receipt.
  const payload = command.payload;
  if (!id) {
    if (payload.legalEntityId !== result.legalEntityId || payload.counterpartyId !== result.counterpartyId
      || payload.assigneeUserId !== (value.assigneeUserId ?? null) || typeof value.documentDate !== "string"
      || String(payload.documentDate).slice(0, 10) !== value.documentDate.slice(0, 10) || !Array.isArray(payload.lines)
      || payload.lines.length !== result.lines.length) return false;
    const inputLines = payload.lines;
    return result.lines.every(line => inputLines.some((raw: unknown) => object(raw) && raw.serviceId === line.serviceId && raw.quantity === line.quantity && raw.unitPriceMinor === line.unitPriceMinor));
  }
  const old = command.expectedDocument;
  if (!old || old.direction !== result.direction || old.legalEntityId !== result.legalEntityId || old.counterpartyId !== result.counterpartyId
    || old.totalMinor !== result.totalMinor || JSON.stringify(sorted(old.lines)) !== JSON.stringify(sorted(result.lines))) return false;
  if (event === "reverse") return result.paidMinor === 0 && result.allocations.length === 0;
  if (event === "post") return old.paidMinor === result.paidMinor && JSON.stringify(sorted(old.allocations)) === JSON.stringify(sorted(result.allocations));
  if (event === "allocations") {
    const added = result.allocations.filter(row => !old.allocations.some(before => before.id === row.id));
    const allocation = added[0];
    return Boolean(added.length === 1 && allocation && allocation.sourceKind === payload.sourceKind && allocation.sourceId === payload.sourceId
      && allocation.amountMinor === payload.amountMinor && result.paidMinor === old.paidMinor + allocation.amountMinor
      && JSON.stringify(sorted(result.allocations.filter(row => row.id !== allocation.id))) === JSON.stringify(sorted(old.allocations)));
  }
  const target = event.split("/")[1], removed = old.allocations.find(row => row.id === target);
  return Boolean(removed && result.paidMinor === old.paidMinor - removed.amountMinor
    && JSON.stringify(sorted(result.allocations)) === JSON.stringify(sorted(old.allocations.filter(row => row.id !== target))));
}
